import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes,
    faSave,
    faSpinner,
    faChartBar,
    faEye,
    faEdit,
    faRotateRight,
    faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import { wgParticipationService } from '../../services/wgParticipationService';
import LoadingSpinner from '../Common/LoadingSpinner';
import './WGParticipationModal.css';

function WGParticipationModal({ show, onHide }) {
    const { user } = useAuth0();
    const [participationStats, setParticipationStats] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // Get minutes until next hour for cache refresh message
    const getMinutesUntilNextSync = () => {
        const now = new Date();
        const minutes = now.getMinutes();
        const minutesUntilNextHour = 60 - minutes;
        return Math.max(minutesUntilNextHour, 1);
    };

    const fetchParticipationStats = async () => {
        setIsRefreshing(true);
        try {
            const data = await wgParticipationService.getWGParticipationStats();
            setParticipationStats(data || []);
        } catch (error) {
            console.error('Error fetching participation stats:', error);
            toast.error('Failed to load participation stats');
        } finally {
            setIsRefreshing(false);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (show) {
            fetchParticipationStats();
        }
    }, [show]);

    const handleEdit = (item) => {
        setEditingItem({
            ...item,
            Title: item.Title || '',
            value: item.value || '',
            icon: item.icon || '',
            color: item.color || '',
            Publish: item.Publish !== undefined ? item.Publish : true
        });
    };

    const handleCancelEdit = () => {
        setEditingItem(null);
    };

    const handleSave = async () => {
        if (!editingItem) return;

        setIsSaving(true);
        try {
            await wgParticipationService.updateWGParticipationStat(
                editingItem.ID,
                editingItem,
                user?.name || 'Admin'
            );

            const minutesToWait = getMinutesUntilNextSync();
            const waitMessage = minutesToWait === 1 
                ? 'Participation stat updated successfully! Changes will be live on the member portal in 1 minute.'
                : `Participation stat updated successfully! Changes will be live on the member portal in ${minutesToWait} minutes.`;
            
            toast.success(waitMessage, {
                autoClose: 6000,
                hideProgressBar: false
            });

            setEditingItem(null);
            await fetchParticipationStats();
        } catch (error) {
            console.error('Error saving participation stat:', error);
            toast.error('Failed to save participation stat');
        } finally {
            setIsSaving(false);
        }
    };

    const handleInputChange = (field, value) => {
        setEditingItem(prev => ({
            ...prev,
            [field]: value
        }));
    };

    if (!show) return null;

    return (
        <div className="wg-participation-modal-overlay">
            <div className="wg-participation-modal">
                <div className="wg-participation-modal-header">
                    <h2>
                        <FontAwesomeIcon icon={faChartBar} />
                        Manage Working Groups Participation Stats
                    </h2>
                    <button className="wg-participation-close-btn" onClick={onHide}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="wg-participation-modal-body">
                    {isLoading ? (
                        <div className="wg-participation-loading">
                            <LoadingSpinner />
                        </div>
                    ) : (
                        <>
                            <div className="wg-participation-controls">
                                <button 
                                    className="wg-participation-btn secondary"
                                    onClick={fetchParticipationStats}
                                    disabled={isRefreshing}
                                >
                                    <FontAwesomeIcon 
                                        icon={faRotateRight} 
                                        spin={isRefreshing}
                                    /> 
                                    {isRefreshing ? 'Refreshing...' : 'Refresh'}
                                </button>
                                <div className="wg-participation-info">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                    Data syncs to member portal every hour
                                </div>
                            </div>

                            <div className="wg-participation-stats-list">
                                {participationStats.map((stat, index) => (
                                    <div key={stat.ID || index} className="wg-participation-stat-card">
                                        {editingItem && editingItem.ID === stat.ID ? (
                                            // Edit mode
                                            <div className="wg-participation-edit-form">
                                                <div className="wg-participation-form-row">
                                                    <div className="wg-participation-form-group">
                                                        <label>Label</label>
                                                        <input
                                                            type="text"
                                                            value={editingItem.Title}
                                                            onChange={(e) => handleInputChange('Title', e.target.value)}
                                                            placeholder="e.g., Working Groups"
                                                        />
                                                    </div>
                                                    <div className="wg-participation-form-group">
                                                        <label>Value</label>
                                                        <input
                                                            type="text"
                                                            value={editingItem.value}
                                                            onChange={(e) => handleInputChange('value', e.target.value)}
                                                            placeholder="e.g., 40 or 88%"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="wg-participation-form-row">
                                                    <div className="wg-participation-form-group">
                                                        <label>Icon Class</label>
                                                        <input
                                                            type="text"
                                                            value={editingItem.icon}
                                                            onChange={(e) => handleInputChange('icon', e.target.value)}
                                                            placeholder="e.g., fa-solid fa-users-line"
                                                        />
                                                    </div>
                                                    <div className="wg-participation-form-group">
                                                        <label>Color</label>
                                                        <input
                                                            type="text"
                                                            value={editingItem.color}
                                                            onChange={(e) => handleInputChange('color', e.target.value)}
                                                            placeholder="e.g., #006d92"
                                                        />
                                                        <div 
                                                            className="wg-participation-color-preview"
                                                            style={{ backgroundColor: editingItem.color }}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="wg-participation-form-row">
                                                    <div className="wg-participation-form-group">
                                                        <label className="wg-participation-checkbox-label">
                                                            <input
                                                                type="checkbox"
                                                                checked={editingItem.Publish}
                                                                onChange={(e) => handleInputChange('Publish', e.target.checked)}
                                                            />
                                                            Published (visible on member portal)
                                                        </label>
                                                    </div>
                                                </div>
                                                <div className="wg-participation-form-actions">
                                                    <button 
                                                        className="wg-participation-btn secondary"
                                                        onClick={handleCancelEdit}
                                                        disabled={isSaving}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button 
                                                        className="wg-participation-btn primary"
                                                        onClick={handleSave}
                                                        disabled={isSaving}
                                                    >
                                                        {isSaving ? (
                                                            <>
                                                                <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FontAwesomeIcon icon={faSave} /> Save Changes
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            // View mode
                                            <div className="wg-participation-stat-view">
                                                <div className="wg-participation-stat-header">
                                                    <div className="wg-participation-stat-icon" style={{ color: stat.color }}>
                                                        <i className={stat.icon}></i>
                                                    </div>
                                                    <div className="wg-participation-stat-info">
                                                        <div className="wg-participation-stat-value" style={{ color: stat.color }}>
                                                            {stat.value}
                                                        </div>
                                                        <div className="wg-participation-stat-label">
                                                            {stat.Title}
                                                        </div>
                                                    </div>
                                                    <div className="wg-participation-stat-status">
                                                        <span className={`wg-participation-status-badge ${stat.Publish ? 'published' : 'draft'}`}>
                                                            {stat.Publish ? 'Published' : 'Draft'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="wg-participation-stat-actions">
                                                    <button 
                                                        className="wg-participation-btn primary small"
                                                        onClick={() => handleEdit(stat)}
                                                    >
                                                        <FontAwesomeIcon icon={faEdit} /> Edit
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {participationStats.length === 0 && !isRefreshing && (
                                <div className="wg-participation-empty">
                                    <p>No participation stats found.</p>
                                    <button 
                                        className="wg-participation-btn secondary"
                                        onClick={fetchParticipationStats}
                                    >
                                        <FontAwesomeIcon icon={faRotateRight} /> Refresh
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="wg-participation-modal-footer">
                    <button className="wg-participation-btn secondary" onClick={onHide}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

export default WGParticipationModal; 